import React, {useState, useEffect, useRef} from 'react';
import './AppHeader.css';
import Tooltip from './ui/Tooltip';
import {IconInfo} from './ui/Icons';

const AppHeader = ({onMenuAction, user}) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const menuRef = useRef(null);

    const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

    const handleMenuClick = (action) => {
        setIsMenuOpen(false);
        onMenuAction && onMenuAction(action);
    };

    // Schließe das Menü, wenn außerhalb geklickt wird
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setIsMenuOpen(false);
            }
        };

        if (isMenuOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isMenuOpen]);

    const displayName = user?.profile?.name || 'Gast';
    const displayUsername = user?.profile?.preferred_username || user?.profile?.sub || '';

    // Extrahiere Rollen aus dem Access Token
    const clientId = process.env.REACT_APP_OIDC_CLIENT_ID;

    let roles = [];
    if (user?.access_token) {
        // JWT dekodieren - der mittlere Teil ist der Payload
        const base64Url = user.access_token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const payload = JSON.parse(atob(base64));

        if (clientId && payload.resource_access && payload.resource_access[clientId]) {
            roles = payload.resource_access[clientId].roles || [];
        }
    }

    const displayRoles = Array.isArray(roles) && roles.length > 0 ? roles.join(', ') : 'keine';
    const hasMultipleRoles = Array.isArray(roles) && roles.length > 1;
    const firstRole = hasMultipleRoles ? roles[0] : null;
    const allRolesText = hasMultipleRoles ? roles.join('\n') : null;

    // Erstelle Tooltip-Text für Mobile-Ansicht
    const userTooltipText = `${displayName}\nNutzer: ${displayUsername}\nRolle: ${displayRoles}`;

    return (
        <header className="app-header">
            <div className="app-header-left">
                <h1 className="app-title">
                    <img src={`${process.env.PUBLIC_URL}/main.png`} alt="RefiReg" className="main-img"/>
                    <span className="logo-text">
            <span className="logo-line-top">Simple</span>
            <span className="logo-line-bottom">RefiReg</span>
          </span>
                </h1>
            </div>
            <div className="app-header-right">
                <div className="user-info">
                    <Tooltip position="bottom" align="right" text={userTooltipText} className="user-avatar-tooltip">
                        <div className="user-avatar">
                            <svg className="user-icon" viewBox="0 0 24 24" fill="currentColor">
                                <path
                                    d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                            </svg>
                        </div>
                    </Tooltip>
                    <div className="user-details">
                        <span className="user-name">{displayName}</span>
                        <span className="user-username-role">Nutzer: {displayUsername}</span>
                        {displayRoles && (
                            <span className="user-username-role">
                            Rolle: {hasMultipleRoles ? (
                                <span style={{display: 'inline-flex', alignItems: 'center', gap: '4px'}}>
                                {firstRole}...
                                <Tooltip position="bottom" align="left" text={allRolesText}>
                                  <IconInfo width={12} height={12} style={{cursor: 'help'}}/>
                                </Tooltip>
                              </span>
                            ) : displayRoles}
                          </span>
                        )}
                    </div>
                </div>
                <div className="hamburger-menu-container" ref={menuRef}>
                    <button className="hamburger-menu-button" onClick={toggleMenu} aria-label="Menü öffnen">
                        <div className="hamburger-line"/>
                        <div className="hamburger-line"/>
                        <div className="hamburger-line"/>
                    </button>
                    {isMenuOpen && (
                        <div className="hamburger-menu-dropdown">
                            <div className="menu-item" onClick={() => handleMenuClick('overview')}>
                                <svg className="menu-icon" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z"/>
                                </svg>
                                <span>Übersicht</span>
                            </div>
                            <div className="menu-item" onClick={() => handleMenuClick('create-transaction')}>
                                <svg className="menu-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                     strokeWidth="2" strokeLinecap="round">
                                    <circle cx="12" cy="12" r="9"/>
                                    <line x1="12" y1="8" x2="12" y2="16"/>
                                    <line x1="8" y1="12" x2="16" y2="12"/>
                                </svg>
                                <span>Transaktion anlegen</span>
                            </div>
                            <div className="menu-separator"/>
                            <div className="menu-item" onClick={() => handleMenuClick('xml-import')}>
                                <svg className="menu-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12"/>
                                </svg>
                                <span>Daten-Import</span>
                            </div>
                            <div className="menu-item" onClick={() => handleMenuClick('audit-log')}>
                                <svg className="menu-icon" viewBox="0 0 24 24" fill="currentColor">
                                    <path
                                        d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
                                </svg>
                                <span>Audit Log</span>
                            </div>
                            <div className="menu-separator"/>
                            <div className="menu-item" onClick={() => handleMenuClick('logout')}>
                                <svg className="menu-icon" viewBox="0 0 24 24" fill="currentColor">
                                    <path
                                        d="M16,17V14H9V10H16V7L21,12L16,17M14,2A2,2 0 0,1 16,4V6H14V4H5V20H14V18H16V20A2,2 0 0,1 14,22H5A2,2 0 0,1 3,20V4A2,2 0 0,1 5,2H14Z"/>
                                </svg>
                                <span>Abmelden</span>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </header>
    );
};

export default AppHeader;

